import { Box, Drawer, Link, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid, type GridColumns, GridOverlay } from '@mui/x-data-grid'
import React from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { ManageClusterAccessDrawerContent } from 'components/configurations/clusters/manage-cluster-access/ManageClusterAccessDrawerContent'
import { useDrawerControlForDataGrid } from 'components/configurations/hooks/use-drawer-control-for-data-grid'
import Icon from 'components/Icon'
import {
  type TeamWithSharedAssetsInformation,
  useManageSharedAssets
} from 'hooks/use-manage-shared-assets'
import { muiTheme } from 'mui-theme'
import { SharedAssetRequestStatus } from 'types/api/sharedAssets/requests'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'

import type { OwnedK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

const ManageClusterAccessTeamsListEmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No teams found</Typography>
    </GridOverlay>
  )
}

export const ManageClusterAccessTeamsList: React.FC<{
  cluster: OwnedK8sClusterAsset
}> = ({ cluster }) => {
  const { getTeamsListWithClusterAccessInformation } = useManageSharedAssets()
  const { isOpen, activeItem, handleRowClick, handleOpen, handleClose } =
    useDrawerControlForDataGrid<{ identifier: string }>()

  const teamsListWithClusterAccessInformation =
    getTeamsListWithClusterAccessInformation(cluster.assetId)

  const columns: GridColumns<TeamWithSharedAssetsInformation> = [
    {
      ...defaultColumnProps,
      field: 'name',
      headerName: 'Team Name',
      flex: 1,
      renderCell: ({ row }) => {
        const { identifier, name, requests } = row

        return (
          <Link onClick={() => handleOpen({ identifier })}>
            <Stack alignItems="center" direction="row" sx={{ columnGap: 0.5 }}>
              {requests[SharedAssetRequestStatus.REQUESTED].length > 0 && (
                <Tooltip
                  placement="top"
                  title="A team is waiting for namespace sharing approval"
                >
                  <Icon
                    color={muiTheme.palette.primary.main}
                    data-testid="pending-cluster-access-request-icon"
                    icon="solidCirclesOverlap"
                  />
                </Tooltip>
              )}
              <TruncatedTextWithTooltip
                tooltipProps={{ title: name }}
                variant="inherit"
              >
                {name}
              </TruncatedTextWithTooltip>
            </Stack>
          </Link>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'sharedNamespaces',
      headerName: 'Number of Shared Namespaces',
      flex: 1,
      renderCell: ({ row }) => {
        const { requests } = row

        return (
          <Typography variant="inherit">
            {requests[SharedAssetRequestStatus.APPROVED].length}
          </Typography>
        )
      }
    }
  ]

  const activeTeam = teamsListWithClusterAccessInformation.find(
    (team) => team.identifier === activeItem?.identifier
  )

  return (
    <Box data-testid="manage-cluster-access-teams-list">
      <Drawer
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 800
          }
        }}
        onClose={handleClose}
      >
        {activeTeam && (
          <ManageClusterAccessDrawerContent
            activeTeam={activeTeam}
            cluster={cluster}
            handleCloseDrawer={handleClose}
          />
        )}
      </Drawer>
      <DataGrid
        {...defaultGridProps}
        columns={columns}
        components={{
          NoRowsOverlay: ManageClusterAccessTeamsListEmptyState
        }}
        getRowId={(value) => value.identifier}
        rows={teamsListWithClusterAccessInformation}
        sx={getGridStyles()}
        onRowClick={handleRowClick}
      />
    </Box>
  )
}
